import {createBridgeCaller, contextHasBridgeFunction} from '../BridgeApi';
import {
    DLCState,
    ProductBridge,
    ProductInstallState,
    ProductResponseCallback
} from '../productBridge';
import {createClassLogger, Logger} from '../../utils/Logging';

const BRIDGE_NAME = 'productinfo';
const logger: Logger = createClassLogger('ProductBridge:Launcher');
const executeProductBridgeCall = createBridgeCaller(BRIDGE_NAME, logger);

interface ProductInfoResponse {
    products: ProductInstallState[];
}

const productLauncher: ProductBridge = {
    getInstalledApps: async (): Promise<ProductInstallState[]> => {
        const response: ProductInfoResponse = await executeProductBridgeCall('getinstalledapps');
        return (response && response.products) || [];
    },

    /**
     * Poll function to get the current state of the product
     */
    getApp: (namespace: string, catalogId: string, appName: string): Promise<ProductInstallState> =>
        executeProductBridgeCall('getapp', [namespace, catalogId, appName]),

    /**
     * Installs the product
     */
    install: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('install', [namespace, catalogId, appName, funnelId]),

    /**
     * Removes the installed product from the machine
     */
    uninstall: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('uninstall', [namespace, catalogId, appName, funnelId]),

    /**
     * Updates the installed product
     */
    update: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('update', [namespace, catalogId, appName, funnelId]),

    /**
     * Pauses the current installation or update in the download queue
     */
    pauseInstallation: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('pauseinstallation', [namespace, catalogId, appName, funnelId]),

    /**
     * Resume the current installation or update in the download queue
     */
    resumeInstallation: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('resumeinstallation', [namespace, catalogId, appName, funnelId]),

    /**
     * Cancels the current installation or update that's in progress and removes it from the download queue,
     * ...this can be resumed as a later action
     */
    cancelInstallation: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('cancelinstallation', [namespace, catalogId, appName, funnelId]),

    /**
     * Launches the product
     *
     * For compatibility, three cases can occur:
     * 1) Launch with no options: call 'launch'
     * 2) Launch with options
     * * a) On a build without launchwithoptions support: call default launch behavior
     * * b) On a buils with launchwithoptions support: call launchwithoptions and pass in options
     */
    launch: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string,
        options?: any
    ): Promise<boolean> => {
        if (options && contextHasBridgeFunction(BRIDGE_NAME, 'launchwithoptions')) {
            return executeProductBridgeCall('launchwithoptions', [
                namespace,
                catalogId,
                appName,
                funnelId,
                options
            ]);
        } else {
            return executeProductBridgeCall('launch', [namespace, catalogId, appName, funnelId]);
        }
    },

    /**
     * If after a product is launched, this will cancel the launch and remove the user from the waiting room
     */
    cancelWaitingRoom: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('cancelwaitingroom', [namespace, catalogId, appName, funnelId]),

    /**
     * Opens the launcher installer options overlay for the specified product
     */
    showInstallerOptions: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('showinstalleroptions', [namespace, catalogId, appName, funnelId]),

    /**
     * Verifies the current products installation
     */
    verifyInstallation: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('verifyinstallation', [namespace, catalogId, appName, funnelId]),

    /**
     * Creates a desktop shortcut for the product (windows only)
     */
    createShortcut: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('createshortcut', [namespace, catalogId, appName, funnelId]),

    /**
     * Gets an array of recently played games in order of most recent order, list may be shorter than maximum amount requested.
     */
    getRecentlyPlayed: async (maxResults: number): Promise<ProductInstallState[]> => {
        const response: ProductInfoResponse = await executeProductBridgeCall('getrecentlyplayed', [
            maxResults
        ]);
        return (response && response.products) || [];
    },

    /**
     * Gets the global auto-update flag which overrides each individual product
     */
    getGlobalAutoUpdate: (): Promise<boolean> => executeProductBridgeCall('getglobalautoupdate'),

    /**
     * Sets the auto-update flag on an installed product
     */
    setAutoUpdate: (
        namespace: string,
        catalogId: string,
        appName: string,
        autoUpdate: boolean,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('setautoupdate', [
            namespace,
            catalogId,
            appName,
            autoUpdate,
            funnelId
        ]),

    /**
     * Gets the value of the auto-update flag on an installed product
     */
    getAutoUpdate: (namespace: string, catalogId: string, appName: string): Promise<boolean> =>
        executeProductBridgeCall('getautoupdate', [namespace, catalogId, appName]),

    /**
     * Sets the cloud save flag on an installed product
     */
    setCloudSave: (
        namespace: string,
        catalogId: string,
        appName: string,
        enableSaveSync: boolean,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('setcloudsave', [
            namespace,
            catalogId,
            appName,
            enableSaveSync,
            funnelId
        ]),

    /**
     * Gets the value of the cloud save flag on an installed product
     */
    getCloudSave: (namespace: string, catalogId: string, appName: string): Promise<boolean> =>
        executeProductBridgeCall('getcloudsave', [namespace, catalogId, appName]),

    /**
     * starts a Sync, useful for users who were playing offline previously
     */
    syncCloudSave: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('synccloudsave', [namespace, catalogId, appName, funnelId]),

    /**
     * Callback that can be registered to know about significant events as they happen
     */
    registerResponseCallback: (callbackfunction: ProductResponseCallback): Promise<void> =>
        executeProductBridgeCall('registerresponsecallback', [callbackfunction]),

    /**
     * Navigates to external support pages
     */
    goToInstallAlert: (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeProductBridgeCall('gotoinstallalert', [namespace, catalogId, appName, funnelId]),

    /**
     * Tells the launcher to re-loaded the builds for a namespace
     */
    refetchUserBuilds: (): Promise<boolean> => executeProductBridgeCall('refetchuserbuilds', ['']),

    /**
     * Starts the install/uninstall flow for a products DLC content based on given DLC configuration state
     * NOTE: namespace, catalogId, appName are for the main product not a DLC
     */
    setAppConfiguration: (
        namespace: string,
        catalogId: string,
        appName: string,
        dlcState: DLCState[]
    ): Promise<boolean> =>
        executeProductBridgeCall('setappconfiguration', [namespace, catalogId, appName, dlcState]),

    /**
     * Gets the DLC configurations for a product
     * NOTE: namespace, catalogId, appName are for the main product not a DLC
     */
    getAppConfiguration: (
        namespace: string,
        catalogId: string,
        appName: string
    ): Promise<DLCState[]> =>
        executeProductBridgeCall('getappconfiguration', [namespace, catalogId, appName])
};

export default productLauncher;
